import { FC, ReactElement } from 'react'
import classNames from 'classnames'

import { Icon, TableCell, TableRow } from '../../../components'
import { IconButton, ValkTable } from '../../../compositions'
import { ValkTalkFeedType, ValkTalkPost } from '../../../entities/ValkTalkPost/ValkTalkPost'
import { getEmployeeAppPostUrl, getValkTalkPostDisplayTitle } from '../../../entities/ValkTalkPost/ValkTalkPostHelpers'
import { formatDate } from '../../../helpers/date'
import trans from '../../../helpers/trans'
import { DateFormat } from '../../../types/dateFormatTypes'

import './ValkTalkPostsTable.scss'

const tableHeaders = [
    '',
    trans('containers.tables.valkTalkPostsTable.post'),
    trans('containers.tables.valkTalkPostsTable.feedType'),
    trans('containers.tables.valkTalkPostsTable.createdAt'),
    '',
]

interface ValkTalkPostsTableProps {
    posts: ValkTalkPost[]
    permittedFeedTypes: ValkTalkFeedType[]
    onEditPostClick: (post: ValkTalkPost) => void
    onDeletePostClick: (post: ValkTalkPost) => void
    className?: string
}

export const ValkTalkPostsTable: FC<ValkTalkPostsTableProps> = ({
    posts,
    permittedFeedTypes,
    onEditPostClick,
    onDeletePostClick,
    className = '',
}): ReactElement => (
    <ValkTable<ValkTalkPost>
        rows={posts}
        tableHeaders={tableHeaders}
        useFlexLayout={false}
        className={`valk-talk-posts-table ${className}`}
    >
        {(postRows): ReactElement[] => postRows.map(post => {
            const title = getValkTalkPostDisplayTitle(post)
            const coverImage = post.images[0]
            const employeeAppPostUrl = getEmployeeAppPostUrl(post.id)
            const canManagePost = permittedFeedTypes.includes(post.feedType)

            return (
                <TableRow hasHoverState key={post.id} className="valk-talk-posts-table__row">
                    <TableCell className="valk-talk-posts-table__thumbnail-cell">
                        {coverImage ? (
                            <img src={coverImage.url} alt="" className="valk-talk-posts-table__thumbnail" />
                        ) : (
                            <span
                                className={classNames('valk-talk-posts-table__thumbnail valk-talk-posts-table__thumbnail--is-placeholder', {
                                    'valk-talk-posts-table__thumbnail--is-announcement': post.feedType === ValkTalkFeedType.valk,
                                })}
                            >
                                <Icon name="bullhorn" className="valk-talk-posts-table__placeholder-icon" />
                            </span>
                        )}
                    </TableCell>

                    <TableCell className="valk-talk-posts-table__title-cell">
                        <span className="valk-talk-posts-table__title">
                            {title || trans('containers.tables.valkTalkPostsTable.imagesOnly', {
                                amount: String(post.images.length),
                            })}
                        </span>
                    </TableCell>

                    <TableCell>
                        {trans(`types.valkTalkFeedType.${post.feedType}.label`)}
                    </TableCell>

                    <TableCell contentClassName="valk-talk-posts-table__created-at">
                        {formatDate(post.createdAt, DateFormat.dateWithHoursMinutes)}
                        {post.author.name && (
                            <span className="valk-talk-posts-table__author">{post.author.name}</span>
                        )}
                    </TableCell>

                    <TableCell className="valk-talk-posts-table__actions-cell" contentClassName="valk-talk-posts-table__actions">
                        {employeeAppPostUrl && (
                            <IconButton
                                hideLabel
                                icon="eye"
                                text={trans('containers.tables.valkTalkPostsTable.viewInEmployeeApp')}
                                onClick={() => window.open(employeeAppPostUrl, '_blank', 'noopener,noreferrer')}
                                iconClassName="valk-talk-posts-table__action-icon"
                            />
                        )}

                        {canManagePost && (
                            <>
                                <IconButton
                                    hideLabel
                                    icon="edit"
                                    text={trans('common.edit')}
                                    onClick={() => onEditPostClick(post)}
                                    iconClassName="valk-talk-posts-table__action-icon"
                                />
                                <IconButton
                                    hideLabel
                                    icon="trash"
                                    text={trans('common.delete')}
                                    onClick={() => onDeletePostClick(post)}
                                    iconClassName="valk-talk-posts-table__action-icon"
                                />
                            </>
                        )}
                    </TableCell>
                </TableRow>
            )
        })}
    </ValkTable>
)
